import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { botApi } from '../api'
import { toApiTimestamp } from '../utils/timeParsing'

export interface EquityPoint {
  time: number
  balance: number
  equity: number
  // Running aggregates emitted by the BacktestEngine. All optional so equity
  // points from older backtests (which don't carry these) still render.
  totalTrades?: number
  winningTrades?: number
  losingTrades?: number
  grossProfit?: number
  grossLoss?: number
  totalCommissionPaid?: number
  maxDrawdownPct?: number
  bestPnl?: number
  worstPnl?: number
  longCount?: number
  shortCount?: number
  longWins?: number
  shortWins?: number
}

/**
 * Snapshot of the running aggregates carried by the most recently emitted
 * equity point. Lets the new Results tab drive a live KPI row + Summary
 * metrics without a separate round-trip.
 */
export interface EquityRunningTotals {
  time: number
  balance: number
  equity: number
  totalTrades: number
  winningTrades: number
  losingTrades: number
  grossProfit: number
  grossLoss: number
  totalCommissionPaid: number
  maxDrawdownPct: number
  bestPnl: number
  worstPnl: number
  longCount: number
  shortCount: number
  longWins: number
  shortWins: number
}

interface UseEquityCurveOptions {
  btid: string
  maxAvailableTs: number | null
}

function toUnixSeconds(dateStr: string): number {
  const utc = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
  return Math.floor(new Date(utc).getTime() / 1000)
}

export default function useEquityCurve({
  btid,
  maxAvailableTs,
}: UseEquityCurveOptions) {
  const [points, setPoints] = useState<EquityPoint[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const lastFetchedTimeRef = useRef<string | null>(null)
  const prevBtidRef = useRef(btid)
  const prevMaxTsRef = useRef(maxAvailableTs)
  const initialFetchDoneRef = useRef(false)
  // Monotonically increasing fetch epoch. Any in-flight fetch with an older
  // epoch is silently discarded on resolve, preventing previous backtest's
  // points from leaking into the current view on btid change.
  const fetchEpochRef = useRef(0)

  const fetchEquity = useCallback(async (
    afterTime: string | null,
    beforeTime: string | null,
  ) => {
    const epoch = ++fetchEpochRef.current
    try {
      const raw = await botApi.getBacktestEquity(
        btid,
        afterTime ?? undefined,
        beforeTime ?? undefined,
      )
      if (fetchEpochRef.current !== epoch) return
      const newPoints: EquityPoint[] = raw.map(r => ({
        time: toUnixSeconds(r.time),
        balance: r.balance,
        equity: r.equity,
        totalTrades: r.total_trades,
        winningTrades: r.winning_trades,
        losingTrades: r.losing_trades,
        grossProfit: r.gross_profit,
        grossLoss: r.gross_loss,
        totalCommissionPaid: r.total_commission_paid,
        maxDrawdownPct: r.max_drawdown_pct,
        bestPnl: r.best_pnl,
        worstPnl: r.worst_pnl,
        longCount: r.long_count,
        shortCount: r.short_count,
        longWins: r.long_wins,
        shortWins: r.short_wins,
      }))

      if (newPoints.length > 0) {
        const lastPoint = newPoints[newPoints.length - 1]
        lastFetchedTimeRef.current = toApiTimestamp(lastPoint.time)
      }

      if (afterTime) {
        setPoints(prev => {
          if (newPoints.length === 0) return prev
          const lastExistingTime = prev.length > 0 ? prev[prev.length - 1].time : 0
          const deduped = newPoints.filter(p => p.time > lastExistingTime)
          if (deduped.length === 0) return prev
          return [...prev, ...deduped]
        })
      } else {
        setPoints(newPoints)
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('Equity curve fetch error:', err)
    }
  }, [btid])

  // Reset state + initial fetch when btid changes. The fetch epoch ensures
  // any older in-flight fetch (from the previous btid) is discarded.
  useEffect(() => {
    const btidChanged = prevBtidRef.current !== btid
    if (btidChanged) {
      prevBtidRef.current = btid
      setPoints([])
      lastFetchedTimeRef.current = null
      initialFetchDoneRef.current = false
      // Sync prevMaxTsRef so the secondary effect doesn't fire a duplicate
      // fetch for the same maxAvailableTs value across the btid swap.
      prevMaxTsRef.current = maxAvailableTs
    }

    const beforeTime = maxAvailableTs !== null ? toApiTimestamp(maxAvailableTs) : null
    setIsLoading(true)
    initialFetchDoneRef.current = true
    const epoch = fetchEpochRef.current + 1
    fetchEquity(null, beforeTime).finally(() => {
      if (fetchEpochRef.current === epoch) setIsLoading(false)
    })
  // Only re-run on btid change. maxAvailableTs changes are handled by the
  // secondary effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btid, fetchEquity])

  // Incremental fetch when maxAvailableTs changes (progress updates)
  useEffect(() => {
    if (!initialFetchDoneRef.current) return

    const prevMax = prevMaxTsRef.current
    prevMaxTsRef.current = maxAvailableTs

    if (prevMax === maxAvailableTs) return

    const afterTime = lastFetchedTimeRef.current
    const beforeTime = maxAvailableTs !== null ? toApiTimestamp(maxAvailableTs) : null

    if (afterTime) {
      fetchEquity(afterTime, beforeTime)
    } else {
      fetchEquity(null, beforeTime)
    }
  }, [maxAvailableTs, fetchEquity])

  // Snapshot of the latest running aggregates (defaults zero so the KPI row
  // always has something to render).
  const runningTotals: EquityRunningTotals | null = useMemo(() => {
    if (points.length === 0) return null
    const last = points[points.length - 1]
    return {
      time: last.time,
      balance: last.balance,
      equity: last.equity,
      totalTrades: last.totalTrades ?? 0,
      winningTrades: last.winningTrades ?? 0,
      losingTrades: last.losingTrades ?? 0,
      grossProfit: last.grossProfit ?? 0,
      grossLoss: last.grossLoss ?? 0,
      totalCommissionPaid: last.totalCommissionPaid ?? 0,
      maxDrawdownPct: last.maxDrawdownPct ?? 0,
      bestPnl: last.bestPnl ?? 0,
      worstPnl: last.worstPnl ?? 0,
      longCount: last.longCount ?? 0,
      shortCount: last.shortCount ?? 0,
      longWins: last.longWins ?? 0,
      shortWins: last.shortWins ?? 0,
    }
  }, [points])

  return { points, isLoading, runningTotals }
}
